'use client'

import { Fragment, useEffect, useRef, useState, type ElementType } from 'react'

import SectionContainer from '@/components/SectionContainer/SectionContainer'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { buildIdFromText } from '@/utils/format'
import { getHashScrollTarget } from '@/components/ContentWithStickyNavigation/ContentWithStickyNavigation'

/* INJECT_SECTIONS_IMPORT */
import AllergenGuide from '@/sections/AllergenGuide/AllergenGuide'
import FeatureSpotlight, {
  FEATURE_SPOTLIGHT_NAV_ID,
  hasRenderableFeatureSpotlight,
} from '@/sections/FeatureSpotlight/FeatureSpotlight'
import DayInTheLifeVideo from '@/sections/DayInTheLifeVideo/DayInTheLifeVideo'
import Locations3UpOpenRoles from '@/sections/Locations3UpOpenRoles/Locations3UpOpenRoles'
import PhotoTextCarousel from '@/sections/PhotoTextCarousel/PhotoTextCarousel'
import AccordionItemsWithSideNavigation from '@/sections/AccordionItemsWithSideNavigation/AccordionItemsWithSideNavigation'
import BlogPost from '@/sections/BlogPost/BlogPost'
import ContactForm from '@/sections/ContactForm/ContactForm'
import ContentCards from '@/sections/ContentCards/ContentCards'
import EcommerceItemList from '@/sections/EcommerceItemList/EcommerceItemList'
import EmailSignup from '@/sections/EmailSignup/EmailSignup'
import EventDetails from '@/sections/EventDetails/EventDetails'
import FeaturedTextBlock from '@/sections/FeaturedTextBlock/FeaturedTextBlock'
import Footer from '@/components/Footer/Footer'
import FourOhFour from '@/sections/FourOhFour/FourOhFour'
import GiftCardGrid from '@/sections/GiftCardGrid/GiftCardGrid'
import GiftCardIframe from '@/sections/GiftCardIframe/GiftCardIframe'
import GiftCards from '@/sections/GiftCards/GiftCards'
import GuestServicesHero from '@/sections/GuestServicesHero/GuestServicesHero'
import HomepageHero from '@/sections/HomepageHero/HomepageHero'
import HomepageHeroLNY from '@/sections/HomepageHeroLNY/HomepageHeroLNY'
import LocationHero from '@/sections/LocationHero/LocationHero'
import LocationsList from '@/sections/LocationsList/LocationsList'
import MediaBackgroundAndTitle from '@/sections/MediaBackgroundAndTitle/MediaBackgroundAndTitle'
import MenuListing from '@/sections/MenuListing/MenuListing'
import MerchandiseListing from '@/sections/MerchandiseListing/MerchandiseListing'
import MerchandiseShowcase from '@/sections/MerchandiseShowcase/MerchandiseShowcase'
import NewsletterSignUpSection from '@/sections/NewsletterSignUpSection/NewsletterSignUpSection'
import OrderInquiryForm from '@/sections/OrderInquiryForm/OrderInquiryForm'
import PressHighlightListing from '@/sections/PressHighlightListing/PressHighlightListing'
import Reviews from '@/sections/Reviews/Reviews'
import RichTextContent from '@/sections/RichTextContent/RichTextContent'
import RichTextHero from '@/sections/RichTextHero/RichTextHero'
import StaggeredImages from '@/sections/StaggeredImages/StaggeredImages'
import Stats from '@/sections/Stats/Stats'
import TextAndAccordion from '@/sections/TextAndAccordion/TextAndAccordion'
import TextAndImage from '@/sections/TextAndImage/TextAndImage'
import TextAndImageHeroWithBreadcrumbs from '@/sections/TextAndImageHeroWithBreadcrumbs/TextAndImageHeroWithBreadcrumbs'
import ThreeUp from '@/sections/ThreeUp/ThreeUp'
import Timeline from '@/sections/Timeline/Timeline'
import ValuesList from '@/sections/ValuesList/ValuesList'

import { SectionsProps } from '@/types/components/SectionsProps'

gsap.registerPlugin(ScrollTrigger)

ScrollTrigger.config({
  ignoreMobileResize: true,
})

const SECTIONS: {
  [key: string]: ElementType
} = {
  /* INJECT_SECTIONS_COMPONENT_TYPE */
  allergenGuide: AllergenGuide,
  featureSpotlight: FeatureSpotlight,
  dayInTheLifeVideo: DayInTheLifeVideo,
  locations3UpOpenRoles: Locations3UpOpenRoles,
  photoTextCarousel: PhotoTextCarousel,
  accordionItemsWithSideNavigation: AccordionItemsWithSideNavigation,
  blogPost: BlogPost,
  contactForm: ContactForm,
  contentCards: ContentCards,
  ecommerceItemList: EcommerceItemList,
  eventDetails: EventDetails,
  emailSignup: EmailSignup,
  featuredTextBlock: FeaturedTextBlock,
  fourOhFour: FourOhFour,
  guestServicesHero: GuestServicesHero,
  giftCardGrid: GiftCardGrid,
  giftCardIframe: GiftCardIframe,
  giftCards: GiftCards,
  homepageHero: HomepageHero,
  homepageHeroLNY: HomepageHeroLNY,
  locationHero: LocationHero,
  locationsList: LocationsList,
  mediaBackgroundAndTitle: MediaBackgroundAndTitle,
  menuListing: MenuListing,
  merchandiseListing: MerchandiseListing,
  merchandiseShowcase: MerchandiseShowcase,
  newsletterSignUpSection: NewsletterSignUpSection,
  orderInquiryForm: OrderInquiryForm,
  pressHighlightListing: PressHighlightListing,
  reviews: Reviews,
  richTextContent: RichTextContent,
  staggeredImages: StaggeredImages,
  stats: Stats,
  richTextHero: RichTextHero,
  textAndAccordion: TextAndAccordion,
  textAndImage: TextAndImage,
  textAndImageHeroWithBreadcrumbs: TextAndImageHeroWithBreadcrumbs,
  timeline: Timeline,
  threeUp: ThreeUp,
  valuesList: ValuesList,
}

function Sections({ sections, yelpData }: SectionsProps) {
  const mounted = useRef(false)

  const sectionsFiltered = sections.filter(
    sectionArr => !sectionArr?.section[0]?.cmsSettings?.isHidden && sectionArr?.section,
  )
  const [allowSectionBackgroundChange, setAllowSectionBackgroundChange] = useState(false)

  const featureSpotlightSection = sectionsFiltered
    .map(sectionArr => sectionArr?.section?.[0])
    .find(section => section?._type === 'featureSpotlight') as SanityFeatureSpotlight | undefined
  const hasMenuListing = sectionsFiltered.some(
    sectionArr => sectionArr?.section?.[0]?._type === 'menuListing',
  )
  // Mirror FeatureSpotlight's own visibility check so we don't register a side-nav entry
  // pointing to an id that won't exist in the DOM (e.g. when dishes get filtered for missing
  // lifestyle images and the section short-circuits to null).
  const featureSpotlightWillRender =
    hasMenuListing && hasRenderableFeatureSpotlight(featureSpotlightSection)
  const featureSpotlightLineItem =
    featureSpotlightWillRender && featureSpotlightSection?.headline
      ? { title: featureSpotlightSection.headline, id: FEATURE_SPOTLIGHT_NAV_ID }
      : null

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      const hash = window.location.hash
      if (!hash) return

      const id = hash.replace('#', '')
      let attempts = 0
      const maxAttempts = 50 // 5 seconds total

      const tryScroll = () => {
        const element = document.getElementById(id)
        if (element) {
          // Wait for layout to settle so the measured position is final.
          setTimeout(() => {
            window.scrollTo({ top: getHashScrollTarget(element), behavior: 'smooth' })
          }, 500)
          return true
        }
        return false
      }

      const attemptScroll = () => {
        if (attempts >= maxAttempts) return

        if (!tryScroll()) {
          attempts++
          setTimeout(attemptScroll, 100)
        }
      }

      // Start attempts after initial render
      setTimeout(attemptScroll, 100)
    }
  }, [])

  // The map below can still skip items (missing/unknown type, hidden, featureSpotlight
  // nested into MenuListing), so the first *rendered* section is not always index 0.
  // Mirror those skip conditions to find it; it drives the data-first-section attribute
  // that SectionContainer's header/banner offset styles key on.
  const rendersSection = (sectionArr: (typeof sectionsFiltered)[number]) => {
    if (!sectionArr?.section) return false
    const sectionObj = { ...sectionArr.section[0], ...(sectionArr.section[0]?.section?.[0] || {}) }
    if (!sectionObj._type || !SECTIONS[sectionObj._type]) return false
    if (sectionObj?.cmsSettings?.isHidden) return false
    if (sectionObj._type === 'featureSpotlight' && hasMenuListing) return false
    return true
  }
  const firstRenderedIndex = sectionsFiltered.findIndex(rendersSection)

  if (!sectionsFiltered?.length) return null

  return (
    <>
      {sectionsFiltered.map((sectionArr, i) => {
        if (!sectionArr?.section) {
          console.warn('No sections tied to data ', sectionArr)
          return null
        }

        const prevSectionObj = { ...sectionsFiltered[i - 1]?.section[0], yelpData }
        const nextSectionObj = { ...sectionsFiltered[i + 1]?.section[0], yelpData }
        const sectionObj = { ...sectionArr?.section[0], ...(sectionArr?.section[0]?.section?.[0] || {}), yelpData }

        if (!sectionObj._type) {
          console.warn('No type found for section ', sectionObj)
          return null
        }

        if (!SECTIONS[sectionObj._type]) {
          console.warn('No section found with type ', sectionObj._type)
          return null
        }

        if (sectionObj?.cmsSettings?.isHidden) {
          console.warn('Section is hidden ', sectionObj)
          return null
        }

        // featureSpotlight is rendered nested inside MenuListing's sticky-nav container so the
        // mobile sticky header covers the spotlight too. Skip the standalone render here.
        if (sectionObj._type === 'featureSpotlight' && hasMenuListing) {
          return null
        }

        const SectionComponent = SECTIONS[sectionObj._type]

        const extraProps =
          sectionObj._type === 'menuListing' && featureSpotlightWillRender
            ? {
                prependLineItems: featureSpotlightLineItem ? [featureSpotlightLineItem] : undefined,
                prependContent: <FeatureSpotlight {...(featureSpotlightSection as SanityFeatureSpotlight)} />,
              }
            : {}

        /* eslint-disable */
        const prevSectionBackground = (prevSectionObj as any)?.sectionBackground
        const currentSectionBackground = (sectionObj as any)?.sectionBackground ?? 'black'
        const nextSectionBackground = (nextSectionObj as any)?.sectionBackground
        const nextSectionType = (nextSectionObj as any)?._type || undefined
        /* eslint-enable */

        return (
          <Fragment key={`${i}_${sectionObj._id}_${buildIdFromText(sectionObj?.cmsSettings?.cmsTitle || '')}`}>
            <SectionContainer
              cmsTitle={sectionObj?.cmsSettings?.cmsTitle || ''}
              _type={sectionObj._type}
              sectionsLength={sectionsFiltered.length}
              sectionBackground={currentSectionBackground}
              previousSectionBackground={prevSectionBackground}
              nextSectionBackground={nextSectionBackground}
              nextSectionType={nextSectionType}
              sectionIndex={i}
              isFirstSection={i === firstRenderedIndex}
              allowSectionBackgroundChange={allowSectionBackgroundChange}
              setAllowSectionBackgroundChange={setAllowSectionBackgroundChange}
            >
              <SectionComponent {...sectionObj} {...extraProps} />
            </SectionContainer>
            {i === sectionsFiltered.length - 1 && (
              <SectionContainer
                sectionIndex={i + 1}
                cmsTitle={'footer'}
                _type={'footer'}
                sectionsLength={sectionsFiltered.length}
                sectionBackground={'black'}
                previousSectionBackground={currentSectionBackground}
                nextSectionBackground={'dough'}
                nextSectionType={undefined}
                allowSectionBackgroundChange={allowSectionBackgroundChange}
                setAllowSectionBackgroundChange={setAllowSectionBackgroundChange}
              >
                <Footer />
              </SectionContainer>
            )}
          </Fragment>
        )
      })}
    </>
  )
}

Sections.displayName = 'Sections'

export default Sections
